import { Button } from '@neo/index'
import { Input } from '@neo/primitives/input'
import { Check, CloudUpload, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useFileUploadMutation } from 'v2/shared/services/queries/file-upload'
import type { RFileUploadResponse } from 'v2/types/api/auth'
import { generateToastsForMutation } from 'v2/utils/toasts'
import z from 'zod'
import { optionalUrl } from '../../config/validation.config'
import { NodevarList } from '../nodevar-list'

const safeAudioUrl = (url: string): string | undefined => {
  try {
    const parsed = new URL(url)
    if (parsed.protocol === 'https:' || parsed.protocol === 'http:') return parsed.href
  } catch {}
  return undefined
}

interface AudioUploadCardProps {
  url: string
  onURLChange: (url: string, name: string) => void
  nodeVars?: string[]
  inputId?: string
}

export const AudioUploadCore: React.FC<AudioUploadCardProps> = ({
  url: dataURL,
  onURLChange,
  nodeVars,
  inputId,
}) => {
  const [url, setUrl] = useState<string>('')
  const { mutate: uploadFile, isPending } = useFileUploadMutation()
  const isNodeVar = url.startsWith('{{')
  const isValid = isNodeVar || z.string().url().safeParse(url).success

  useEffect(() => {
    setUrl(dataURL)
  }, [dataURL])

  const {
    getInputProps,
    open: fileDialogOpen,
    getRootProps,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple: false,
    accept: {
      'audio/mpeg': ['.mp3'],
      'audio/wav': ['.wav'],
    },
    onDrop: (acceptedFiles) => {
      const formData = new FormData()
      formData.append('file', acceptedFiles[0])
      uploadFile(
        formData,
        generateToastsForMutation(
          {
            title: 'Success',
            description: 'Audio file uploaded successfully',
            callback: (data) => {
              onURLChange(
                (data as RFileUploadResponse).file_url,
                (data as RFileUploadResponse).file_name,
              )
            },
          },
          {
            title: 'Error',
            description: 'Failed to upload audio file',
          },
        ),
      )
    },
  })

  if (dataURL)
    return (
      <div className="p-3 flex gap-5 border border-border bg-card rounded place-items-center">
        <div className="min-w-0 flex-1">
          <audio
            src={safeAudioUrl(dataURL)}
            controls
            className="w-full max-w-full dark:[color-scheme:dark]"
          />
        </div>
        <X size={24} className="cursor-pointer" onClick={() => onURLChange('', '')} />
      </div>
    )
  return (
    <div className="flex gap-2 flex-col items-center">
      <div className="w-full" {...getRootProps()}>
        <input {...getInputProps()} />
        <Button
          className="w-full p-4 flex gap-4 h-auto text-left text-tertiary justify-start"
          type="button"
          onClick={fileDialogOpen}
          variant="outline"
        >
          <CloudUpload />
          {isPending ? (
            <div>Uploading...</div>
          ) : (
            <div>
              <div className="font-inter text-xs-500 normal-case tracking-normal">
                Drag files or click to upload
              </div>
              <div className="font-inter text-xs-400 normal-case tracking-normal">
                Supported files: Audio
              </div>
            </div>
          )}
        </Button>
      </div>
      <span className="text-tertiary">or</span>
      <Input
        endIcon={
          <div className="flex items-center">
            {nodeVars && nodeVars.length > 0 && (
              <NodevarList
                nodevars={nodeVars}
                className="pr-2"
                inputElementSelector={inputId ? `[data-input-id="${inputId}"]` : undefined}
              />
            )}
            <Button
              type="button"
              onClick={() => isValid && onURLChange(url, 'audio-upload.mp3')}
              className="rounded-l-none relative -right-1"
              disabled={!isValid}
            >
              <Check size={16} />
            </Button>
          </div>
        }
        placeholder={nodeVars ? 'Enter URL or use node variable' : 'Enter URL'}
        inputClassName="font-jetbrains text-xs-400"
        value={url}
        onChange={(e) => {
          setUrl(e.target.value)
        }}
        data-input-id={inputId}
        errorText={
          !url || isNodeVar || optionalUrl.safeParse(url).success
            ? ''
            : 'Please enter valid audio URL'
        }
      />
    </div>
  )
}
